<template>
    <div
        style="position: relative;"
        :class="[
            { 'required-field-placeholder-hint': required && !inputValue },
            inputStyle === 'tailwind' ? 'input-style-tailwind' : '',
        ]"
    >
        <template v-if="inputStyle === 'tailwind'">
            <div class="space-y-1.5">
                <label v-if="label" class="text-xs font-semibold text-slate-500 uppercase tracking-wider">{{ label }}</label>
                <div class="relative">
                    <input
                        ref="textField"
                        type="text"
                        :value="inputValue"
                        :disabled="disabled"
                        :placeholder="displayPlaceholder"
                        :class="[twInputClass, errorMessages && errorMessages.length ? 'border-red-500' : '']"
                        @input="handleInput($event.target.value)"
                        @focus="onFocus"
                        @blur="onBlur"
                        @keydown="onKeydown"
                    />
                    <v-progress-circular
                        v-if="loading"
                        indeterminate
                        size="20"
                        width="2"
                        class="absolute right-3 top-1/2 transform -translate-y-1/2"
                    />
                </div>
                <p v-if="errorMessages && errorMessages.length" class="text-xs text-red-600">{{ errorMessages[0] }}</p>
            </div>
        </template>
        <v-text-field
            v-else
            ref="textField"
            v-model="inputValue"
            :disabled="disabled"
            :error-messages="errorMessages"
            :label="label"
            :placeholder="displayPlaceholder"
            :persistent-placeholder="required"
            :dense="dense"
            :outlined="outlined"
            :clearable="clearable"
            :loading="loading"
            hide-details="auto"
            @input="handleInput"
            @focus="onFocus"
            @blur="onBlur"
            @keydown="onKeydown"
        />

        <!-- Suggestion unique pour SIREN -->
        <div 
            v-if="showSuggestion && companyData" 
            class="suggestion-dropdown"
            style="position: absolute; top: 100%; left: 0; right: 0; z-index: 1000; background: white; border: 1px solid #ccc; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); min-width: 400px; max-width: 600px;"
        >
            <v-list dense>
                <v-list-item
                    @click="selectCompany"
                    @mouseenter="isHovered = true"
                    @mouseleave="isHovered = false"
                    :style="{ 
                        cursor: 'pointer', 
                        padding: '12px 16px',
                        backgroundColor: isHovered ? '#f5f5f5' : 'white'
                    }"
                >
                    <v-list-item-content>
                        <v-list-item-title style="font-weight: 500; margin-bottom: 4px;">
                            {{ companyData.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle style="font-size: 0.8rem; color: #666; line-height: 1.4;">
                            <div v-if="companyData.siren" style="margin-bottom: 2px;">
                                <strong>SIREN:</strong> {{ companyData.siren }}
                            </div>
                            <div v-if="companyData.line1 || companyData.line2 || companyData.city || companyData.postal_code || companyData.country" style="margin-bottom: 2px;">
                                <strong>Adresse:</strong> 
                                <span v-if="companyData.line1">{{ companyData.line1 }}</span>
                                <span v-if="companyData.line2">, {{ companyData.line2 }}</span>
                                <span v-if="(companyData.line1 || companyData.line2) && (companyData.city || companyData.postal_code)">, </span>
                                <span v-if="companyData.city">{{ companyData.city }}</span>
                                <span v-if="companyData.postal_code"> {{ companyData.postal_code }}</span>
                                <span v-if="companyData.country && (companyData.city || companyData.postal_code)">, </span>
                                <span v-if="companyData.country">{{ companyData.country }}</span>
                            </div>
                            <div v-if="companyData.vat_number" style="margin-bottom: 2px;">
                                <strong>TVA:</strong> {{ companyData.vat_number }}
                            </div>
                        </v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import { requiredPlaceholder } from "@/utils/required-field";

export default {
    name: 'CompanySiren',
    props: {
        value: {
            type: String,
            default: ''
        },
        errorMessages: {
            type: Array,
            default: () => []
        },
        label: {
            type: String,
            default: 'SIREN'
        },
        placeholder: {
            type: String,
            default: 'Entrez un SIREN (9 chiffres)...'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: true
        },
        /** 'vuetify' (default) or 'tailwind' – controls input visual style */
        inputStyle: {
            type: String,
            default: 'vuetify',
            validator: (v) => ['vuetify', 'tailwind'].includes(v),
        },
        required: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            inputValue: this.value || '',
            companyData: null,
            loading: false,
            debounceTimer: null,
            showSuggestion: false,
            isFocused: false,
            isHovered: false
        };
    },
    computed: {
        displayPlaceholder() {
            if (!this.required) {
                return this.placeholder;
            }
            return requiredPlaceholder(this.placeholder, this.$translate("requiredSuffix"));
        },
        twInputClass() {
            return 'w-full bg-slate-50 border border-slate-200 dark:border-slate-700 rounded text-sm px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
        },
    },
    methods: {
        handleInput(value) {
            this.inputValue = value;
            this.$emit('input', value);
            
            // Strip spaces from the value for validation
            const strippedValue = value ? value.replace(/\s/g, '') : '';
            // Vérifier si c'est un SIREN valide (9 chiffres)
            if (strippedValue && strippedValue.length === 9 && /^\d{9}$/.test(strippedValue)) {
                this.performSirenSearch(strippedValue);
            } else {
                this.companyData = null;
                this.showSuggestion = false;
            }
        },
        onFocus() {
            this.isFocused = true;
            if (this.companyData) {
                this.showSuggestion = true;
            }
        },
        onBlur() {
            this.isFocused = false;
            // Délai pour permettre le clic sur la suggestion
            setTimeout(() => {
                this.showSuggestion = false;
            }, 200);
        },
        onKeydown(event) {
            if (event.key === 'Enter' && this.companyData) {
                event.preventDefault();
                this.selectCompany();
            } else if (event.key === 'Escape') {
                this.showSuggestion = false;
            }
        },
        selectCompany() {
            if (this.companyData) {
                this.$emit('company-selected', this.companyData);
                this.showSuggestion = false;
            }
        },
        performSirenSearch(siren) {
            // Strip spaces from SIREN
            const strippedSiren = siren ? siren.replace(/\s/g, '') : '';
            
            if (!strippedSiren || strippedSiren.length !== 9 || !/^\d{9}$/.test(strippedSiren)) {
                return;
            }

            // Debounce the search
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }

            this.debounceTimer = setTimeout(() => {
                this.searchCompanyBySiren(strippedSiren);
            }, 500); // Debounce plus court pour SIREN
        },
        async searchCompanyBySiren(siren) {
            // Strip spaces from SIREN as a safety measure
            const strippedSiren = siren ? siren.replace(/\s/g, '') : '';
            
            if (!strippedSiren || strippedSiren.length !== 9 || !/^\d{9}$/.test(strippedSiren)) {
                this.companyData = null;
                this.showSuggestion = false;
                return;
            }

            this.loading = true;
            this.companyData = null;
            this.showSuggestion = false;

            try {
                const response = await axios.get('/api/company/info/', {
                    params: {
                        query: strippedSiren
                    }
                });

                this.loading = false;
                
                if (response.data) {
                    this.companyData = response.data;
                    // Afficher la suggestion si le champ est focusé
                    if (this.isFocused) {
                        this.showSuggestion = true;
                    }
                } else {
                    this.companyData = null;
                    this.showSuggestion = false;
                }
            } catch (error) {
                this.loading = false;
                console.error('Error fetching company by SIREN:', error);
                this.companyData = null;
                this.showSuggestion = false;
            }
        }
    },
    watch: {
        value(newValue) {
            if (newValue !== this.inputValue) {
                this.inputValue = newValue;
            }
        }
    },
    i18n: {
        messages: {
            en: {
                requiredSuffix: 'Required',
                company_siren: {
                    placeholder: 'Enter a SIREN (9 digits)...',
                    no_results: 'No company found for this SIREN',
                    search_error: 'Error searching company by SIREN'
                }
            },
            fr: {
                requiredSuffix: 'Requis',
                company_siren: {
                    placeholder: 'Entrez un SIREN (9 chiffres)...',
                    no_results: 'Aucune société trouvée pour ce SIREN',
                    search_error: 'Erreur lors de la recherche de société par SIREN'
                }
            }
        }
    }
};
</script>

<style scoped>
.suggestion-dropdown {
    z-index: 1000;
    min-width: 400px;
    max-width: 600px;
}

.suggestion-dropdown .v-list-item {
    border-bottom: 1px solid #f0f0f0;
}

.suggestion-dropdown .v-list-item:hover {
    background-color: #f5f5f5;
}

/* Force visible border on native input */
.input-style-tailwind input {
    border-width: 1px !important;
    border-style: solid !important;
    border-color: #e2e8f0 !important;
}
.input-style-tailwind input:focus {
    border-color: #3b82f6 !important;
}
</style>
